import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  HeicConversionError,
  decodeHeicToPng,
  downloadBlob,
  encodeImageBlob,
  outputFilename,
} from '@/features/heic-converter/model/heicProcessing'
import {
  OpenImageViewerShell,
  loadNativeImage,
  type OpenImageViewerActionContext,
  type OpenImageViewerConfig,
} from '@/features/open-image-viewer'
import { useT } from '@/shared/i18n'

type ExportFormat = 'jpg' | 'png'

const UTILITY_KEY = 'heic-viewer'
const JPG_EXPORT_QUALITY = 0.92

function HeicExportActions({ context }: { context: OpenImageViewerActionContext }) {
  const t = useT()
  const copy = t.heicViewer
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const [error, setError] = useState<string | null>(null)
  const operationRef = useRef<AbortController | null>(null)

  useEffect(() => () => {
    operationRef.current?.abort()
  }, [])

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      const pngBlob = context.image.exportBlob
      if (!pngBlob) return

      setExporting(format)
      setError(null)
      const controller = new AbortController()
      operationRef.current = controller
      toolInvoke(UTILITY_KEY, { format })
      const startTime = performance.now()

      try {
        const blob =
          format === 'png'
            ? pngBlob
            : await encodeImageBlob(
                pngBlob,
                'image/jpeg',
                JPG_EXPORT_QUALITY,
                copy.errorsText,
                controller.signal,
              )
        if (controller.signal.aborted) return
        downloadBlob(blob, outputFilename(context.file.name, format))
        toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), { format })
      } catch (caught) {
        const code = caught instanceof HeicConversionError ? caught.code : 'conversion_failed'
        if (code === 'cancelled' || controller.signal.aborted) return
        toolError(UTILITY_KEY, code, { format })
        setError(caught instanceof Error ? caught.message : copy.errorsText.conversion)
      } finally {
        if (!controller.signal.aborted) setExporting(null)
      }
    },
    [context, copy.errorsText],
  )

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2">
        <Button
          type="button"
          disabled={exporting !== null}
          onClick={() => void handleExport('jpg')}
        >
          {exporting === 'jpg' ? copy.exportingJpg : copy.downloadJpg}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={exporting !== null}
          onClick={() => void handleExport('png')}
        >
          {exporting === 'png' ? copy.exportingPng : copy.downloadPng}
        </Button>
      </div>
      {error && (
        <p className="text-sm leading-5 text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export function HeicViewerPage() {
  const t = useT()
  const copy = t.heicViewer
  const config: OpenImageViewerConfig = {
    legacyAnalyticsKey: UTILITY_KEY,
    accept: '.heic,.heif,image/heic,image/heif,image/heic-sequence,image/heif-sequence',
    acceptedMimeTypes: ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'],
    acceptedExtensions: ['.heic', '.heif'],
    maxFileSizeBytes: 50 * 1024 * 1024,
    formatLabel: 'HEIC',
    loadImage: async (file, signal) => {
      const { blob, frames } = await decodeHeicToPng(file, copy.errorsText, signal)
      const decodedFile = new File([blob], file.name, { type: 'image/png' })
      const image = await loadNativeImage(decodedFile, config, copy, signal)
      return {
        ...image,
        mimeLabel: file.type || 'image/heic',
        notice: frames > 1 ? copy.multiFrameWarning : undefined,
        exportBlob: blob,
      }
    },
    renderActions: (context) => <HeicExportActions context={context} />,
    convertHref: '/heic-v-jpg',
    relatedTools: copy.relatedTools,
  }

  return <OpenImageViewerShell copy={copy} config={config} />
}
